import React, { FC, BaseSyntheticEvent, useMemo, ReactNode } from 'react';

import {
  Box,
  HStack,
  Stack,
  Button,
  ButtonGroup,
  Flex,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import cloneDeep from 'lodash/cloneDeep';
import keys from 'lodash/keys';
import map from 'lodash/map';
import merge from 'lodash/merge';
import { FormProvider, useForm, UseFormProps } from 'react-hook-form';
import { ObjectSchema } from 'yup';

import { PageHeaderWithIcon } from 'components/DataDisplay';
import { PrimaryButton } from 'components/DataEntry';
import { IconType } from 'components/Icons';
import { Drawer } from 'components/Overlay';
import { DrawerStyles } from 'components/Overlay/Drawer/types';

import { useFormComponent } from '../hooks/useFormComponent';
import { StyleCtx } from '../hooks/useStyles';
import { defaultStyles } from '../styles';
import { FormStyles, ButtonOptionProps, FormSchema } from '../types';

const defaultButtonOptions: ButtonOptionProps = {
  submit: { isVisible: true, name: 'SUBMIT' },
  reset: { isVisible: false, name: 'CANCEL', onClick: () => null },
  back: { isVisible: false, name: 'BACK', onClick: () => null },
};

export interface FormProps {
  isLoading?: boolean;
  title?: ReactNode;
  titleIcon?: ReactNode;
  iconType?: IconType;
  isTitleIconFilled?: boolean;
  schema?: FormSchema;
  validationSchema?: any;
  overwriteDefaultStyles?: boolean;
  formOptions?: UseFormProps;
  buttonOptions?: ButtonOptionProps;
  styles?: FormStyles;
  handleSubmit: (values: any, e?: BaseSyntheticEvent) => void;
  handleBackSubmit?: (values: any, e?: BaseSyntheticEvent) => void;
  submitButtonRef?: React.RefObject<HTMLButtonElement>;
  showHelp?: boolean;
  helpDrawer?: {
    isOpen: boolean;
    onClose();
    content: ReactNode;
    header?: ReactNode;
    styles?: DrawerStyles;
  };
  formValidation?: ObjectSchema<any>;
  children?: ReactNode | ReactNode[];
}

export const Form: FC<FormProps> = ({
  isLoading,
  title,
  titleIcon,
  iconType,
  isTitleIconFilled = true,
  schema = {},
  formOptions,
  handleSubmit,
  handleBackSubmit,
  submitButtonRef,
  buttonOptions,
  overwriteDefaultStyles,
  styles = {},
  showHelp,
  children,
  formValidation,
  helpDrawer,
}) => {
  let form = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    ...(!!formValidation ? { resolver: yupResolver(formValidation) } : {}),
    shouldFocusError: true,
    criteriaMode: 'all',
    ...formOptions,
  });

  const { renderField } = useFormComponent();

  const value = form.watch();
  const submitted = { ...form.formState };

  const baseStyles = useMemo(() => {
    const props = cloneDeep(defaultStyles);
    return overwriteDefaultStyles ? styles : merge(props, styles);
  }, [styles, overwriteDefaultStyles]);

  const buttonProps = useMemo(() => {
    const props = cloneDeep(defaultButtonOptions);
    return merge(props, buttonOptions);
  }, [buttonOptions]);

  const { reset, back, submit, customButtonOptions } = buttonProps;

  return (
    <HStack
      w="full"
      h="full"
      spacing={4}
      justify="space-between"
      align={'start'}
    >
      <Box w={baseStyles.form?.formWidth}>
        <StyleCtx.Provider value={baseStyles}>
          <FormProvider {...form}>
            <Box w="full" as="form" onSubmit={form.handleSubmit(handleSubmit)}>
              <Box {...baseStyles.form?.container}>
                {!!title && (
                  <Box w="full" pb={6}>
                    <PageHeaderWithIcon
                      label={title}
                      reversed
                      icon={titleIcon}
                      iconType={iconType}
                      iconFilled={isTitleIconFilled}
                      {...baseStyles.form?.title}
                    />
                  </Box>
                )}
                <Box {...baseStyles.form?.inputWrapper}>
                  <Stack spacing={baseStyles.form?.fieldSpacing}>
                    {children && children}
                    {map(Object.entries(schema), renderField(isLoading))}
                  </Stack>
                </Box>
                <Box>
                  <ButtonGroup {...baseStyles.form?.buttonGroup}>
                    {reset && reset?.isVisible && (
                      <Button
                        type="reset"
                        onClick={reset?.onClick}
                        isDisabled={reset?.isDisabled}
                        isLoading={reset?.isLoading}
                        {...baseStyles.form?.resetButton}
                      >
                        {reset.name}
                      </Button>
                    )}
                    <ButtonGroup {...baseStyles.form?.submitButtonGroup}>
                      {back && back?.isVisible && (
                        <Button
                          type="button"
                          {...baseStyles.form?.backButton}
                          onClick={
                            handleBackSubmit !== undefined
                              ? () => handleBackSubmit(value)
                              : back.onClick
                          }
                          isDisabled={back.isDisabled}
                          isLoading={back.isLoading}
                        >
                          {back.name}
                        </Button>
                      )}
                      {customButtonOptions &&
                        map(
                          customButtonOptions,
                          (o, index) =>
                            o.isVisible && (
                              // Added a key to the mapped buttons.
                              <Button key={o.name ?? index} size="sm" {...o}>
                                {o.name}
                              </Button>
                            ),
                        )}

                      {submit && submit?.isVisible && (
                        <PrimaryButton
                          ref={submitButtonRef}
                          type="submit"
                          isLoading={
                            isLoading ||
                            submit?.isLoading ||
                            submitted.isSubmitting
                          }
                          tooltip={submit?.tooltip}
                          tooltipStyles={
                            baseStyles?.form?.submitButton?.tooltipStyles
                          }
                          isDisabled={
                            isLoading ||
                            submit?.isDisabled ||
                            submitted.isSubmitting ||
                            !!keys(form?.formState?.errors).length
                          }
                          {...baseStyles.form?.submitButton}
                        >
                          {submit?.name}
                        </PrimaryButton>
                      )}
                    </ButtonGroup>
                  </ButtonGroup>
                </Box>
              </Box>
            </Box>
          </FormProvider>
        </StyleCtx.Provider>
      </Box>

      {helpDrawer?.isOpen && (
        <Drawer
          isOpen={helpDrawer?.isOpen}
          onClose={helpDrawer?.onClose}
          closeButton
          body={helpDrawer?.content}
          header={helpDrawer?.header}
          styles={{ drawer: { size: 'xl' }, ...helpDrawer?.styles }}
        />
      )}

      {showHelp && (
        <Flex {...baseStyles.form?.helpWrapper}>
          {map(
            schema,
            (each, key) =>
              each?.isHelpOpen && (
                // Added a key to the mapped help panels.
                <Box key={key} {...baseStyles.form?.helpComponent}>
                  <Box>{each?.helpComponent}</Box>
                </Box>
              ),
          )}
        </Flex>
      )}
    </HStack>
  );
};
